import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/ui-brand";
import { GoogleButton } from "@/components/GoogleButton";
import { TermsCheckbox } from "@/components/TermsCheckbox";
import { checks, collect } from "@/lib/validation";

import { Field } from "@/components/panel";

export default function Reclamar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginToken } = useAuth();
  const token = new URLSearchParams(location.search).get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState({ p1: "", p2: "" });
  const [visible, setVisible] = useState(false);
  const [acepta, setAcepta] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState({});

  useEffect(() => {
    if (!token) { setError("Falta el token de activación en el enlace."); return; }
    api.get(`/auth/claim/${token}`).then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "El enlace es inválido o venció."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = collect({ p1: checks.password(pw.p1), p2: checks.match(pw.p1, pw.p2),
      terminos: acepta ? null : "Para activar tu cuenta tenés que aceptar los Términos y Condiciones de uso" });
    setErr(errs);
    if (Object.keys(errs).length) return toast.error(Object.values(errs)[0]);
    setBusy(true);
    try {
      const r = await api.post("/auth/claim", { token, password: pw.p1, visible_para_empresas: visible, acepta_terminos: acepta });
      await loginToken(r.data.access_token, r.data.refresh_token);
      toast.success("¡Cuenta activada!");
      navigate("/egresado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo activar la cuenta");
    } finally { setBusy(false); }
  };

  if (error) return (
    <Section className="py-14 max-w-md">
      <div className="card-brand p-7" data-testid="claim-error">
        <h1 className="h-section text-xl mb-2">Enlace no válido</h1>
        <p className="text-sm text-[var(--brand-text-muted)]">{error}</p>
        <p className="text-sm mt-3">Pedile a tu academia un nuevo enlace de activación.</p>
      </div>
    </Section>
  );
  if (!data) return <Section className="py-14 max-w-md"><p className="font-heading text-[var(--brand-primary)]">Cargando…</p></Section>;

  return (
    <Section className="py-14 max-w-md">
      <div className="card-brand p-7 sm:p-8" data-testid="claim-form">
        <h1 className="h-section text-2xl mb-1">Activá tu cuenta</h1>
        <p className="text-sm text-[var(--brand-text-muted)] mb-5">Hola {data.name?.split(" ")[0]}, revisá tus datos y creá tu acceso.</p>
        <div className="rounded-xl bg-[var(--brand-surface)] p-4 text-sm mb-5 space-y-1" data-testid="claim-prefill">
          <p><b>Email:</b> {data.email}</p>
          {data.ubicacion && <p><b>Localidad:</b> {data.ubicacion}</p>}
          {data.telefono && <p><b>Teléfono:</b> {data.telefono}</p>}
          {data.formacion?.[0]?.programa && <p><b>Formación:</b> {data.formacion[0].programa}</p>}
          {data.carga_horaria && <p><b>Carga horaria:</b> {data.carga_horaria}</p>}
        </div>
        <form onSubmit={submit} noValidate className="space-y-3">
          <Field label="Creá tu contraseña" type="password" error={err.p1} value={pw.p1} onChange={(e) => setPw({ ...pw, p1: e.target.value })} placeholder="Mín. 8 caracteres, con letras y números" data-testid="claim-password" />
          <Field label="Repetí la contraseña" type="password" error={err.p2} value={pw.p2} onChange={(e) => setPw({ ...pw, p2: e.target.value })} data-testid="claim-password2" />
          <label className="flex items-start gap-2 py-2 cursor-pointer" data-testid="claim-consent">
            <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} className="mt-1" data-testid="claim-consent-checkbox" />
            <span className="text-xs text-[var(--brand-text-muted)]">Autorizo que las empresas de la red vean mi perfil y las fortalezas que elija publicar, para conectarme con oportunidades laborales. Puedo desactivarlo cuando quiera.</span>
          </label>
          <TermsCheckbox checked={acepta} onChange={(v) => { setAcepta(v); setErr(({ terminos: _t, ...r }) => r); }} error={err.terminos} testid="claim-terms" />
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="claim-submit">{busy ? "Activando…" : "Activar mi cuenta"}</button>
        </form>
        <GoogleButton label="Continuar con Google" claimToken={token} visible={visible} aceptaTerminos={acepta} testid="claim-google" />
      </div>
    </Section>
  );
}
